import { Link, useLocation } from 'react-router-dom';
import {
  Building2,
  ChevronDown,
  FileText,
  FlaskConical,
  Globe,
  Home,
  LogOut,
  Shield,
  User,
} from 'lucide-react';
import { ROUTES } from '@/config/routes';
import { useAuth } from '@/hooks/use-auth';
import { useTenant } from '@/contexts/tenant-context';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SyncStatusBadge } from '@/components/sync/sync-status-badge';

const NAV_ITEMS = [
  { label: 'Dashboard', path: ROUTES.DASHBOARD, icon: Home },
  { label: 'Jobs', path: ROUTES.JOBS, icon: Building2 },
  { label: 'Reports', path: ROUTES.REPORTS, icon: FileText },
  { label: 'Analyzer', path: ROUTES.ANALYZER, icon: FlaskConical },
];

/**
 * Desktop top navigation header.
 *
 * Displays the StructureAI logo, main navigation links,
 * sync status indicator, and user avatar dropdown.
 */
export function DesktopHeader() {
  const { user, signOut, isAdmin, isSuperAdmin } = useAuth();
  const { tenants, activeTenantId, activeTenantName, isOverriding, switchTenant, resetTenant } = useTenant();
  const location = useLocation();

  const initials = user?.displayName
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) ?? 'U';

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur-xl supports-[backdrop-filter]:bg-background/80">
      <div className="flex h-16 items-center px-6 lg:px-8">
        {/* Logo and brand */}
        <Link
          to={ROUTES.DASHBOARD}
          className="mr-8 flex items-center gap-3 group"
        >
          <img
            src="/structureai-logo.png"
            alt="StructureAI"
            className="h-9 object-contain transition-opacity group-hover:opacity-80"
          />
        </Link>

        {/* Main navigation */}
        <nav className="flex items-center gap-1">
          {NAV_ITEMS.map((item) => {
            const isActive =
              item.path === ROUTES.DASHBOARD
                ? location.pathname === '/'
                : location.pathname.startsWith(item.path);

            return (
              <Link key={item.path} to={item.path}>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    'gap-2 text-sm font-medium transition-all duration-150',
                    isActive
                      ? 'bg-primary/10 text-primary hover:bg-primary/15 font-semibold'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
                  )}
                >
                  <item.icon className={cn('h-4 w-4', isActive && 'text-primary')} />
                  <span className="hidden lg:inline">{item.label}</span>
                </Button>
              </Link>
            );
          })}
        </nav>

        {/* Right side: tenant switcher + sync status + user menu */}
        <div className="ml-auto flex items-center gap-3">
          {/* Tenant switcher for super-admins */}
          {isSuperAdmin && tenants.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant={isOverriding ? 'default' : 'outline'}
                  size="sm"
                  className={cn(
                    'gap-1.5 text-xs h-8 max-w-[200px]',
                    isOverriding && 'bg-amber-600 hover:bg-amber-700 text-white',
                  )}
                >
                  <Building2 className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{activeTenantName}</span>
                  <ChevronDown className="h-3 w-3 shrink-0 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  Switch Company
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {tenants.map((t) => (
                  <DropdownMenuItem
                    key={t.id}
                    className={cn(
                      'cursor-pointer text-sm',
                      t.id === activeTenantId && 'bg-primary/10 font-medium',
                    )}
                    onClick={() => switchTenant(t.id)}
                  >
                    <Building2 className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                    {t.name}
                    {t.id === activeTenantId && (
                      <span className="ml-auto text-[10px] text-primary">Active</span>
                    )}
                  </DropdownMenuItem>
                ))}
                {isOverriding && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="cursor-pointer text-xs text-muted-foreground"
                      onClick={resetTenant}
                    >
                      Reset to home tenant
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <SyncStatusBadge />

          <div className="h-6 w-px bg-border/60" />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="relative h-9 w-9 rounded-full ring-1 ring-border/50 hover:ring-primary/30 transition-all"
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage
                    src={user?.photoURL ?? undefined}
                    alt={user?.displayName ?? 'User'}
                  />
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent className="w-60" align="end" forceMount>
              <DropdownMenuLabel className="font-normal p-3">
                <div className="flex flex-col space-y-1.5">
                  <p className="text-sm font-semibold leading-none">
                    {user?.displayName}
                  </p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {user?.email}
                  </p>
                </div>
              </DropdownMenuLabel>

              <DropdownMenuSeparator />

              <DropdownMenuGroup>
                <Link to={ROUTES.PROFILE}>
                  <DropdownMenuItem className="cursor-pointer py-2">
                    <User className="mr-2.5 h-4 w-4 text-muted-foreground" />
                    <span>Profile</span>
                  </DropdownMenuItem>
                </Link>
              </DropdownMenuGroup>

              {isAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <Link to={ROUTES.ADMIN}>
                    <DropdownMenuItem className="cursor-pointer py-2">
                      <Shield className="mr-2.5 h-4 w-4 text-muted-foreground" />
                      <span>Admin Panel</span>
                    </DropdownMenuItem>
                  </Link>
                </>
              )}

              {isSuperAdmin && (
                <Link to={ROUTES.PLATFORM}>
                  <DropdownMenuItem className="cursor-pointer py-2">
                    <Globe className="mr-2.5 h-4 w-4 text-muted-foreground" />
                    <span>Platform</span>
                  </DropdownMenuItem>
                </Link>
              )}

              <DropdownMenuSeparator />

              <DropdownMenuItem
                onClick={signOut}
                className="text-destructive focus:text-destructive cursor-pointer py-2"
              >
                <LogOut className="mr-2.5 h-4 w-4" />
                <span>Sign out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
